import { useParams, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Loader from "../layouts/components/Loader";
import AuthModal from "../layouts/components/AuthModal";
import "./ProductPage.css";

// Cart Icon Component
function CartIcon({
  size = 18,
  strokeWidth = 1.8,
  color = "#fff",
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {/* PLUS above cart */}
      <line
        x1="12" y1="4" x2="12" y2="8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <line
        x1="10" y1="6" x2="14" y2="6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />

      {/* Cart outline */}
      <path
        d="M6 8h13l-1.2 6.5a2 2 0 0 1-2 1.5H8a2 2 0 0 1-2-1.5L4 4H2"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Wheels */}
      <circle cx="9" cy="20" r="1.6" fill={color} />
      <circle cx="18" cy="20" r="1.6" fill={color} />
    </svg>
  );
}

const API_BASE = "https://api.mariamlyluxe.com/public";

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("auth_token") ||
  "";

export default function ProductPage() {
  const { idOrSlug } = useParams();
  const nav = useNavigate();

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [relatedCartLoading, setRelatedCartLoading] = useState(null);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [bannerImage, setBannerImage] = useState(null);
  
  // Review system states
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    comment: '',
    stars_count: 5
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();
        const baseHeaders = { Accept: "application/json" };
        const authHeaders = token
          ? { ...baseHeaders, Authorization: `Bearer ${token}` }
          : baseHeaders;

        // try by slug/id (single endpoint)
        const url = `${API_BASE}/api/products/${encodeURIComponent(idOrSlug)}`;
        let r = await fetch(url, { headers: authHeaders });

        if (!r.ok) throw new Error(`Failed: ${r.status}`);
        const j = await r.json();
        const product = j?.product ?? j;
        if (cancelled) return;
        setP(product);
        
        // Set variants and default selection (already ordered by backend)
        if (product?.variants && product.variants.length > 0) {
          // Variants are already ordered by sort_order from backend
          const orderedVariants = [...product.variants].sort((a, b) => {
            const orderA = a.sort_order || 0;
            const orderB = b.sort_order || 0;
            if (orderA !== orderB) return orderA - orderB;
            return a.id - b.id; // Fallback to ID for consistent ordering
          });
          
          setProductVariants(orderedVariants);
          setSelectedVariant(orderedVariants[0]); // Select first variant by default
        }

        // (A) If user is logged-in and you prefer explicit tracking, call the small endpoint:
        if (token && product?.id) {
          // this is optional; your backend show() already logs when GET is authenticated.
          fetch(`${API_BASE}/api/recently-viewed`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
            body: JSON.stringify({ product_id: product.id }),
          }).catch(() => {});
        }

        // wishlist check
        if (token && product?.id) {
          try {
            const wishlistRes = await fetch(`${API_BASE}/api/wishlist`, {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
              },
            });
            if (wishlistRes.ok) {
              const wishlistData = await wishlistRes.json();
              const wishlistItems = wishlistData?.wishlist || [];
              const isW = wishlistItems.some(
                (item) =>
                  item.product_id === product.id.toString() ||
                  item.product_id === product.id
              );
              if (!cancelled) setIsInWishlist(isW);
            }
          } catch (e) {
            console.warn("Failed to fetch wishlist:", e);
          }
        }

        // related
        if (product?.id) {
          setRelatedLoading(true);
          try {
            const rr = await fetch(
              `${API_BASE}/api/products/${product.id}/related`,
              { headers: baseHeaders }
            );
            if (rr.ok) {
              const data = await rr.json();
              const rel =
                data?.related_products ??
                data?.products ??
                (Array.isArray(data) ? data : []);
              if (!cancelled) setRelatedProducts(rel);
            }
          } finally {
            if (!cancelled) setRelatedLoading(false);
          }
        }

        // Load reviews
        if (product?.id) {
          loadReviews(product.id);
        }
      } catch (e) {
        console.warn(e);
        if (!cancelled) setError("Product not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [idOrSlug]);

  // Add to cart (current product)
  const handleAddToCart = async () => {
    if (!p) return;
    const token = getToken();
    if (!token) {
      toast.error("Please login to add items to cart");
      return;
    }
    try {
      setCartLoading(true);
      
      // Prepare cart data with variant information
      const cartData = {
        product_id: p.id,
        quantity,
        ...(selectedVariant && { variant_id: selectedVariant.id })
      };
      
      const response = await fetch(`${API_BASE}/api/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify(cartData),
      });

      if (response.ok) {
        const successData = await response.json();
        const variantText = selectedVariant ? ` (${selectedVariant.color})` : '';
        toast.success(successData.message || `Product${variantText} added to cart successfully!`);
        setQuantity(1);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to add product to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setCartLoading(false);
    }
  };


const loadBannerImage = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/pages-banners/product`, {
        headers: { Accept: "application/json" }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.banner && data.banner.image_url) {
          setBannerImage(data.banner.image_url);
        }
      }
    } catch (e) {
      console.warn("Failed to load banner image:", e);
      // Fallback to default image if API fails
      setBannerImage('../images/crosswalk.jpg');
    }
  };
  
  useEffect(() => {
    loadBannerImage();
  }, []);
  // Add a related product to cart (qty=1)
  const addRelatedToCart = async (prodId) => {
    const token = getToken();
    if (!token) {
      toast.error("Please login to add items to cart");
      return;
    }
    try {
      setRelatedCartLoading(prodId);
      const response = await fetch(`${API_BASE}/api/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify({ product_id: prodId, quantity: 1 }),
      });
      if (response.ok) {
        const successData = await response.json();
        toast.success(successData.message || "Added to cart!");
        // Optional: Go home (you had this behavior)
        setTimeout(() => {
          nav("/");
        }, 3000);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to add to cart");
      }
    } catch (e) {
      console.error(e);
      toast.error("Network error. Please try again.");
    } finally {
      setRelatedCartLoading(null);
    }
  };

  // Add to wishlist
  const handleAddToWishlist = async () => {
    if (!p) return;
    const token = getToken();
    if (!token) {
      toast.error("Please login to add items to wishlist");
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/wishlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify({ product_id: p.id }),
      });

      if (response.ok) {
        const successData = await response.json();
        setIsInWishlist(true);
        toast.success(successData.message || "Product added to wishlist!");
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to add product to wishlist");
      }
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      toast.error("Network error. Please try again.");
    }
  };

  // Load reviews for the product
  const loadReviews = async (productId) => {
    setReviewsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/products/${productId}/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.data || []);
        console.log(data.data);
      }
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Submit review
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!p) return;
    
    const token = getToken();
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    if (!reviewForm.comment.trim()) {
      toast.error("Please write a review comment");
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await fetch(`${API_BASE}/api/reviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify({
          product_id: p.id,
          comment: reviewForm.comment.trim(),
          stars_count: reviewForm.stars_count
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success("Review submitted successfully! It will be reviewed by admin.");
        setReviewForm({ comment: '', stars_count: 5 });
        setUserHasReviewed(true);
        // Reload reviews to show the new one (if admin approved it)
        loadReviews(p.id);
      } else {
        if (response.status === 409) {
          toast.error("You have already reviewed this product");
          setUserHasReviewed(true);
        } else {
          toast.error(data.message || "Failed to submit review");
        }
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  // Handle successful login from AuthModal
  const handleLoginSuccess = () => {
    setShowAuthModal(false);
  };

  // Star rating component
  const StarRating = ({ rating, interactive = false, onRatingChange }) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star ${star <= rating ? 'filled' : ''} ${interactive ? 'interactive' : ''}`}
            onClick={interactive ? () => onRatingChange(star) : undefined}
            disabled={!interactive}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="ppg-loading">
        <Loader size={48} label="Loading product..." />
        <p>Loading product...</p>
      </div>
    );
  }
  if (error || !p) return <p style={{ color: "crimson" }}>{error || "Not found."}</p>;

  // Get images based on selected variant or default product images
  const getCurrentImages = () => {
    if (selectedVariant && selectedVariant.images && selectedVariant.images.length > 0) {
      return selectedVariant.images;
    }
    return p.images || [];
  };

  const allImages = getCurrentImages();
  const mainImage = allImages[selectedImageIndex]?.url || p.image_url || null;

  // Handle variant selection
  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setSelectedImageIndex(0); // Reset to first image of new variant
  };

  // Get effective pricing - use variant pricing if available, otherwise product pricing
  const getEffectivePricing = () => {
    if (selectedVariant) {
      return {
        regularPrice: selectedVariant.regular_price != null
          ? Number(selectedVariant.regular_price)
          : (p.regular_price != null ? Number(p.regular_price) : 0),
        sellingPrice: selectedVariant.selling_price != null
          ? Number(selectedVariant.selling_price)
          : (p.selling_price != null ? Number(p.selling_price) : 0),
        discount: selectedVariant.discount != null
          ? Number(selectedVariant.discount)
          : (p.discount != null ? Number(p.discount) : 0),
      };
    }
    return {
      regularPrice: p.regular_price != null ? Number(p.regular_price) : 0,
      sellingPrice: p.selling_price != null ? Number(p.selling_price) : 0,
      discount: p.discount != null ? Number(p.discount) : 0,
    };
  };

  const { regularPrice, sellingPrice, discount } = getEffectivePricing();
  
  // Get stock quantity - use variant quantity if available, otherwise product quantity
  const getStockQuantity = () => {
    if (selectedVariant && selectedVariant.quantity !== undefined && selectedVariant.quantity !== null) {
      return Number(selectedVariant.quantity);
    }
    return Number(p.quantity || 0);
  };
  
  const stockNum = getStockQuantity();

  return (
    <div className="ppg">
      <div 
        className="ppg-banner"
        style={{
          backgroundImage: bannerImage ? `url(${bannerImage})` : 'url(../images/crosswalk.jpg)'
        }}
      >
        <div className="ppg-banner-content">
          <h1 className="ppg-banner-title">{p.name}</h1>
          <nav className="ppg-breadcrumb">
            <a href="/">Mariamly</a>
            <span className="ppg-breadcrumb-separator">›</span>
            <a href="/shop">Shop</a>
            <span className="ppg-breadcrumb-separator">›</span>
            <span className="ppg-breadcrumb-current">{p.name}</span>
          </nav>
        </div>
      </div>

      <div className="ppg-wrap">
        {/* main row */}
        <div className="ppg-main">
          {/* LEFT: images */}
          <div className="ppg-images">
            <div className="ppg-hero">
              {mainImage ? (
                <img src={mainImage} alt={p.name} />
              ) : (
                <div className="ppg-noimg">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <span>No image</span>
                </div>
              )}
            </div>

            {allImages.length > 1 && (
              <div className="ppg-thumbnails">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    className={`ppg-thumbnail ${selectedImageIndex === index ? "active" : ""}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={image.url} alt={`${p.name} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: info */}
          <div className="ppg-info">
            <div className="ppg-title-row">
              <h1 className="ppg-title">{p.name}</h1>
              <button
                className="ppg-heart"
                aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                onClick={handleAddToWishlist}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill={isInWishlist ? "currentColor" : "none"}>
                  <path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Price section (only if we have a valid selling price) */}
            {sellingPrice > 0 && (
            <div className="ppg-price-section">
              {stockNum === 0 && (
                <div className="ppg-out-of-stock-banner">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Out of Stock
                </div>
              )}
              {regularPrice > sellingPrice ? (
                <>
                  <div
                    className="ppg-price-old"
                    style={{ color: '#e74c3c', textDecoration: 'line-through' }}
                  >
                    ${regularPrice.toFixed(2)}
                  </div>
                  <div className="ppg-price-new">${sellingPrice.toFixed(2)}</div>
                  {discount > 0 && (
                    <div className="ppg-discount">
                      Save {(regularPrice - sellingPrice).toFixed(2)} ({discount}% off)
                    </div>
                  )}
                </>
              ) : (
                <div className="ppg-price">${sellingPrice.toFixed(2)}</div>
              )}
            </div>
            )}

            {/* Variants (Color and Size) */}
            {productVariants.length > 0 && (
              <div className="ppg-variants">
                <div className="ppg-variants-label">
                  Shades: {selectedVariant?.color || selectedVariant?.size || 'Select Variant'}
                </div>
                <div className="ppg-variants-radio-group">
                  {productVariants.map((variant) => {
                    const isColorVariant = variant.color && variant.hex_color;
                    const isSelected = selectedVariant?.id === variant.id;
                    const isOutOfStock = Number(variant.quantity || 0) === 0;
                    
                    return (
                      <label key={variant.id} className={`ppg-variant-radio ${isColorVariant ? 'color-variant' : ''} ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}>
                        <input
                          type="radio"
                          name="variant"
                          value={variant.id}
                          checked={isSelected}
                          onChange={() => handleVariantSelect(variant)}
                          disabled={isOutOfStock}
                        />
                        {isColorVariant ? (
                          <div className="ppg-color-swatch">
                            <div 
                              className="color-circle"
                              style={{ backgroundColor: variant.hex_color }}
                            >
                              {isOutOfStock && <div className="out-of-stock-line"></div>}
                            </div>
                          </div>
                        ) : (
                          <span className="ppg-variant-radio-label">
                            <div className="variant-name">{variant.size || variant.color || 'Variant'}</div>
                            {variant.regular_price && (
                              <div className="variant-price">
                                {variant.discount > 0 ? (
                                  <>
                                    <span className="original-price" style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px' }}>
                                      ${Number(variant.regular_price).toFixed(2)}
                                    </span>
                                    <span className="sale-price" style={{ color: '#e74c3c' }}>
                                      ${Number(variant.selling_price).toFixed(2)}
                                    </span>
                                    <div className="discount-badge" style={{ 
                                      background: '#e74c3c', 
                                      color: 'white', 
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      borderRadius: '10px',
                                      marginLeft: '8px',
                                      display: 'inline-block'
                                    }}>
                                      {variant.discount}% OFF
                                    </div>
                                  </>
                                ) : (
                                  <span>${Number(variant.selling_price || variant.regular_price).toFixed(2)}</span>
                                )}
                              </div>
                            )}
                            {isOutOfStock && <span className="out-of-stock-text">Out of Stock</span>}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="ppg-mini">
              <strong>{p.name}</strong>{" "}
            </div>

            {p.desc && (
              <p className="ppg-desc">
                {p.desc}
              </p>
            )}

            {/* Weight Display */}
            {p.weight && (
              <div className="ppg-weight">
                <span className="ppg-weight-label">Weight:</span>
                <span className="ppg-weight-value">{p.weight}</span>
              </div>
            )}

            {/* SKU Display - Show variant SKU if available, otherwise product SKU */}
            {(selectedVariant?.sku || p.sku) && (
              <div className="ppg-sku">
                <span className="ppg-sku-label">SKU:</span>
                <span className="ppg-sku-value">{selectedVariant?.sku || p.sku}</span>
              </div>
            )}

            {/* Tags Display */}
            {p.tags && p.tags.trim() && (
              <div className="ppg-tags">
                <span className="ppg-tags-label">Tags:</span>
                <div className="ppg-tags-list">
                  {console.log('Product tags:', p.tags)}
                  {p.tags.split(',').filter(tag => tag.trim()).map((tag, index) => (
                    <span key={index} className="ppg-tag">
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="ppg-perks">
              <div className="perk">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Cash on delivery
              </div>
              <div className="perk">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M2 8h16l2 4v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6l2-4z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="6" cy="18" r="2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="18" cy="18" r="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M8 8h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Ship to all Lebanon
              </div>
              {/* {stockNum > 0 && stockNum < 6 && (
                <div className="perk low">
                  Low stock! 
                </div>
              )} */}
            </div>

            <div className={`qty-label ${stockNum === 0 ? 'out-of-stock-label' : ''}`}>
              {stockNum === 0 ? 'Out of Stock' : 'Quantity'}
            </div>
            <div className="ppg-actions">
              <div className={`qty ${stockNum === 0 ? 'disabled' : ''}`}>
                <button
                  className="qbtn"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1 || stockNum === 0}
                  aria-label="Decrease"
                >
                  –
                </button>
                <div className="qval">{quantity}</div>
                <button
                  className="qbtn"
                  onClick={() => setQuantity(Math.min(stockNum || 99, quantity + 1))}
                  disabled={stockNum > 0 ? quantity >= stockNum : false}
                  aria-label="Increase"
                >
                  +
                </button>
              </div>

              <button className={`cta ${stockNum === 0 ? 'out-of-stock' : ''}`} disabled={stockNum === 0 || cartLoading} onClick={handleAddToCart}>
                {cartLoading ? (
                  <Loader size={16} />
                ) : stockNum === 0 ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Out of Stock
                  </>
                ) : (
                  <>
                    <CartIcon size={16} color="#fff" />
                    Add to Cart{selectedVariant ? ` - ${selectedVariant.color && selectedVariant.size ? `${selectedVariant.color} - ${selectedVariant.size}` : selectedVariant.color || selectedVariant.size}` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Ingredients and Usage Instructions - Full Width */}
        {(p.ingredients || p.usage_instructions) && (
          <div className="ppg-full-width-sections">
            {/* Ingredients Section */}
            {p.ingredients && (
              <div className="ppg-info-block">
                <h2 className="ppg-h2">Ingredients</h2>
                <div className="ppg-ingredients">
                  <p>{p.ingredients}</p>
                </div>
              </div>
            )}

            {/* Usage Instructions Section */}
            {p.usage_instructions && (
              <div className="ppg-info-block">
                <h2 className="ppg-h2">How to Use</h2>
                <div className="ppg-usage">
                  <p>{p.usage_instructions}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reviews Section */}
        <div className="ppg-reviews">
          <div className="ppg-reviews-header">
            <h2 className="ppg-reviews-title">Customer Reviews</h2>
            <div className="ppg-reviews-stats">
              {reviews.length > 0 && (
                <div className="ppg-reviews-summary">
                  <StarRating rating={Math.round(reviews.reduce((sum, r) => sum + r.stars_count, 0) / reviews.length)} />
                  <span className="ppg-reviews-count">({reviews.length} review{reviews.length !== 1 ? 's' : ''})</span>
                </div>
              )}
            </div>
          </div>

          {/* Review Form - Only for logged-in users who haven't reviewed */}
          {getToken() && !userHasReviewed ? (
            <div className="ppg-review-form">
              <h3>Write a Review</h3>
              <form onSubmit={handleSubmitReview}>
                <div className="ppg-review-rating">
                  <label>Rating:</label>
                  <StarRating 
                    rating={reviewForm.stars_count} 
                    interactive={true} 
                    onRatingChange={(rating) => setReviewForm(prev => ({ ...prev, stars_count: rating }))} 
                  />
                </div>
                <div className="ppg-review-comment">
                  <label htmlFor="review-comment">Your Review:</label>
                  <textarea
                    id="review-comment"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Share your experience with this product..."
                    rows={4}
                    maxLength={1000}
                    required
                  />
                  <div className="ppg-review-char-count">
                    {reviewForm.comment.length}/1000 characters
                  </div>
                </div>
                <button 
                  type="submit" 
                  className="ppg-review-submit"
                  disabled={submittingReview || !reviewForm.comment.trim()}
                >
                  {submittingReview ? <Loader size={16} /> : 'Submit Review'}
                </button>
              </form>
            </div>
          ) : !getToken() ? (
            <div className="ppg-review-login-prompt">
              <h3>Write a Review</h3>
              <div className="ppg-review-login-content">
                <p>Please log in to write a review for this product.</p>
                <button 
                  className="ppg-review-login-btn"
                  onClick={() => setShowAuthModal(true)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="10,17 15,12 10,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="15" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Login to Review
                </button>
              </div>
            </div>
          ) : null}

          {/* Reviews List */}
          <div className="ppg-reviews-list">
            {reviewsLoading ? (
              <div className="ppg-reviews-loading">
                <Loader size={32} label="Loading reviews..." />
              </div>
            ) : reviews.length > 0 ? (
              reviews.map((review) => (
                <div key={review.id} className="ppg-review-item">
                  <div className="ppg-review-header">
                    <div className="ppg-review-user">
                      <span className="ppg-review-name">{review.user_name}</span>
                      <StarRating rating={review.stars_count} />
                    </div>
                    <div className="ppg-review-date">
                      {new Date(review.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="ppg-review-content">
                    <p>{review.comment}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="ppg-reviews-empty">
                <p>No reviews yet. Be the first to review this product!</p>
              </div>
            )}
          </div>
        </div>

        {/* Related (4) */}
        {relatedProducts.length > 0 && (
          <div className="ppg-related">
            <div className="ppg-related-header">
              <h2 className="ppg-related-title">Related Products</h2>
            </div>
            {relatedLoading ? (
              <div className="ppg-rel-loading">
                <Loader size={32} label="Loading related..." />
              </div>
            ) : (
              <div className="rel-grid">
                {relatedProducts.slice(0, 4).map((rp) => {
                  const img =
                    rp.image_url || rp.images?.[0]?.url || rp.images?.[0]?.image_url || null;
                  const sell = Number(rp.selling_price || 0);
                  const reg = Number(rp.regular_price || 0);
                  const save = reg > sell ? reg - sell : 0;

                  return (
                    <div 
                      className="rel-card v3" 
                      key={rp.id}
                      onClick={() => nav(`/product/${rp.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="rel-thumb">
                        {img ? <img src={img} alt={rp.name} /> : <div className="rel-noimg">No image</div>}
                      </div>

                      {/* row: name + cart */}
                      <div className="rel-head-row">
                        <h3 className="rel-title">{rp.name}</h3>
                        <button
                          className={`rel-cart-btn ${Number(rp.quantity || 0) === 0 ? 'out-of-stock' : ''}`}
                          aria-label={Number(rp.quantity || 0) === 0 ? "Out of stock" : "Add to cart"}
                          disabled={relatedCartLoading === rp.id || Number(rp.quantity || 0) === 0}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click when clicking cart button
                            addRelatedToCart(rp.id);
                          }}
                        >
                          {relatedCartLoading === rp.id ? (
                            <Loader size={16} />
                          ) : Number(rp.quantity || 0) === 0 ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <CartIcon size={20} color="#fff" />
                          )}
                        </button>
                      </div>

                      {/* row: regular + selling + save */}
                      <div className="rel-price-row">
                        <div className="rel-price-left">
                          {reg > sell && <span className="rel-old" style={{ color: '#e74c3c', textDecoration: 'line-through' }}>${reg.toFixed(2)}</span>}
                          <span className="rel-new">${sell.toFixed(2)}</span>
                        </div>
                        {save > 0 && (
                          <span className="rel-save-text">
                            Save ${save.toFixed(2)}
                          </span>
                        )}
                      </div>
                      
                      {/* Out of stock indicator for related products */}
                      {Number(rp.quantity || 0) === 0 && (
                        <div className="rel-out-of-stock">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Out of Stock
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Admin Dashboard Button */}
        {getToken() && (
          <div className="ppg-admin-section">
            <button 
              className="ppg-admin-btn"
              onClick={() => nav('/admin')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Admin Dashboard
            </button>
          </div>
        )}
      </div>

      {/* AuthModal component */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
}
